import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react'

interface ScreenerExchangeSelectionOptions {
  activeTicker: string | null
  dataTicker: string | null | undefined
  availableExchanges: string[]
  hasHydratedPrefs: boolean
  savedExchanges?: string[] | null
  preferenceKey?: string
  setSelectedExchanges: Dispatch<SetStateAction<string[]>>
}

export function useScreenerExchangeSelection({
  activeTicker,
  dataTicker,
  availableExchanges,
  hasHydratedPrefs,
  savedExchanges,
  preferenceKey,
  setSelectedExchanges,
}: ScreenerExchangeSelectionOptions) {
  const previousTicker = useRef<string | null>(null)
  const previousPreferenceKey = useRef(preferenceKey)
  const needsSelectionReset = useRef(true)
  const seenExchanges = useRef(new Set<string>())
  const previousAvailableExchanges = useRef<string[]>([])
  const rememberedSelections = useRef(new Set<string>())

  useEffect(() => {
    const ticker = activeTicker?.trim().toUpperCase() || null
    if (ticker !== previousTicker.current || preferenceKey !== previousPreferenceKey.current) {
      previousTicker.current = ticker
      previousPreferenceKey.current = preferenceKey
      needsSelectionReset.current = true
      seenExchanges.current = new Set()
      previousAvailableExchanges.current = []
      rememberedSelections.current = new Set()
    }

    // The old coin's data can remain visible while the new request is loading.
    // Keep the reset pending until the requested coin has chartable exchanges.
    if (
      !hasHydratedPrefs
      || !ticker
      || dataTicker?.trim().toUpperCase() !== ticker
      || availableExchanges.length === 0
    ) return

    const resetSelection = needsSelectionReset.current
    needsSelectionReset.current = false
    const previouslyAvailable = previousAvailableExchanges.current
    const previouslySeen = new Set(seenExchanges.current)
    previousAvailableExchanges.current = availableExchanges
    for (const exchange of availableExchanges) seenExchanges.current.add(exchange)
    setSelectedExchanges((current) => {
      // Preserve the last choice for temporarily missing history. An exchange
      // explicitly deselected before disappearing must remain deselected.
      const remembered = resetSelection ? new Set<string>() : new Set(rememberedSelections.current)
      for (const exchange of previouslyAvailable) {
        if (current.includes(exchange)) remembered.add(exchange)
        else remembered.delete(exchange)
      }
      const selectedByDefault = (exchange: string) => savedExchanges == null || savedExchanges.includes(exchange)
      const next = resetSelection
        ? availableExchanges.filter(selectedByDefault)
        : [
            ...current.filter((exchange) => availableExchanges.includes(exchange)),
            ...availableExchanges.filter((exchange) => (
              !current.includes(exchange)
              && !previouslyAvailable.includes(exchange)
              && (previouslySeen.has(exchange) ? remembered.has(exchange) : selectedByDefault(exchange))
            )),
          ]
      for (const exchange of availableExchanges) {
        if (next.includes(exchange)) remembered.add(exchange)
        else remembered.delete(exchange)
      }
      rememberedSelections.current = remembered
      return next.length === current.length && next.every((exchange, index) => exchange === current[index])
        ? current
        : next
    })
  }, [activeTicker, dataTicker, availableExchanges, hasHydratedPrefs, savedExchanges, preferenceKey, setSelectedExchanges])
}
